import {
  JIRA_CONFIG_PROBLEM_CODE,
  JIRA_CONNECTION_HEALTH_ISSUE,
  JIRA_MAPPING_PROBLEM_CODE,
  JIRA_STATUS_MAPPING_PROBLEM_CATEGORY,
  JIRA_STATUS_MAPPING_PROBLEM_LABELS,
  isJiraStatusNotMappedProblemCode
} from './jiraIntegrationDetail.constants';

/**
 * Copy for the status mapping card while the connection is broken. Mapping health is unknown
 * then, so the card must not claim mappings are valid.
 */
export function getJiraStatusMappingPausedSubtitle(healthIssue) {
  switch (healthIssue) {
    case JIRA_CONNECTION_HEALTH_ISSUE.AUTH_EXPIRED:
      return JIRA_STATUS_MAPPING_PROBLEM_LABELS.PAUSED_AUTH_EXPIRED;
    case JIRA_CONNECTION_HEALTH_ISSUE.APP_REMOVED:
      return JIRA_STATUS_MAPPING_PROBLEM_LABELS.PAUSED_APP_REMOVED;
    case JIRA_CONNECTION_HEALTH_ISSUE.DISCONNECTED:
    default:
      return JIRA_STATUS_MAPPING_PROBLEM_LABELS.PAUSED_DISCONNECTED;
  }
}

export function getJiraConnectedSpaceElementId(spaceId) {
  return spaceId ? `jira-connected-space-${spaceId}` : null;
}

/** Resolve a connected space id from a focus target (id, project key, and/or name). */
export function resolveConnectedSpaceIdFromFocus(connectedSpaces = [], focus) {
  if (!focus || !connectedSpaces.length) {
    return null;
  }

  if (focus.spaceId) {
    const byId = connectedSpaces.find(space => space.id === focus.spaceId);
    if (byId) {
      return byId.id;
    }
  }

  if (focus.projectKey) {
    const key = normalizeMatchValue(focus.projectKey);
    const byKey = connectedSpaces.find(space => normalizeMatchValue(space.projectKey) === key);
    if (byKey) {
      return byKey.id;
    }
  }

  if (focus.spaceName) {
    const name = normalizeMatchValue(focus.spaceName);
    const byName = connectedSpaces.find(space => normalizeMatchValue(space.name) === name);
    if (byName) {
      return byName.id;
    }
  }

  return null;
}

export function buildJiraSpaceBrowseUrl(instanceUrl, projectKey) {
  if (!instanceUrl || !projectKey) {
    return null;
  }

  const host = instanceUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
  return `https://${host}/browse/${projectKey}`;
}

export function getJiraStatusMappingProblemCopy({ code, jiraStatusName, jiraIssueTypeName, spaceName, detail }) {
  const status = jiraStatusName || 'Unknown';
  const statusLabel = jiraIssueTypeName ? `"${status}" (${jiraIssueTypeName})` : `"${status}"`;
  const space = spaceName || 'this space';

  if (isJiraStatusNotMappedProblemCode(code)) {
    return {
      prefix: `Jira status ${statusLabel} has no NetRise mapping - sync events are being skipped. `,
      linkLabel: `Fix in ${space}`,
      suffix: ''
    };
  }

  switch (code) {
    case JIRA_MAPPING_PROBLEM_CODE.STATUS_NOT_FOUND:
      return {
        prefix: `${statusLabel} was removed or renamed in Jira. Remap in `,
        linkLabel: space,
        suffix: ' to resume sync.'
      };
    case JIRA_MAPPING_PROBLEM_CODE.ILLEGAL_TRANSITION:
      return {
        prefix: `Jira status ${statusLabel} cannot be reached in this workflow. `,
        linkLabel: `Fix in ${space}`,
        suffix: ''
      };
    case JIRA_CONFIG_PROBLEM_CODE.JIRA_API_ERROR:
      return {
        prefix: detail || `Unable to validate status mappings for ${space}.`,
        linkLabel: null,
        suffix: ''
      };
    default:
      return {
        prefix: detail || `Status mapping problem in ${space}.`,
        linkLabel: null,
        suffix: ''
      };
  }
}

export function flattenJiraStatusMappingProblems(spaces = [], instanceUrl) {
  return spaces.flatMap(space => {
    const spaceUrl = buildJiraSpaceBrowseUrl(instanceUrl, space.projectKey);
    const mappingProblems = (space.mappingProblems ?? [])
      // public-api already omits subtasks; keep a client guard for stale responses.
      .filter(problem => !isSubtaskMappingProblem(problem))
      .map((problem, index) => ({
        id: `mapping:${space.jiraSpaceId}:${problem.jiraIssueTypeId || ''}:${problem.jiraStatusMappingId || problem.jiraStatusId || index}:${index}`,
        category: JIRA_STATUS_MAPPING_PROBLEM_CATEGORY.STATUS_MAPPING,
        code: problem.code,
        jiraStatusName: problem.jiraStatusName,
        jiraIssueTypeName: problem.jiraIssueTypeName ?? null,
        spaceName: space.projectName,
        spaceId: space.jiraSpaceId,
        projectKey: space.projectKey,
        spaceUrl,
        detectedAt: problem.detectedAt,
        detail: problem.detail
      }));
    const otherProblems = (space.spaceProblems ?? []).map((problem, index) => ({
      id: `other:${space.jiraSpaceId}:${problem.code}:${index}`,
      category: JIRA_STATUS_MAPPING_PROBLEM_CATEGORY.OTHER,
      code: problem.code,
      jiraStatusName: null,
      jiraIssueTypeName: null,
      spaceName: space.projectName,
      spaceId: space.jiraSpaceId,
      projectKey: space.projectKey,
      spaceUrl,
      detectedAt: problem.detectedAt,
      detail: problem.detail
    }));

    return [...mappingProblems, ...otherProblems];
  });
}

function isSubtaskMappingProblem(problem) {
  // public-api omits subtasks; name check covers stale responses that still include them.
  const issueTypeName = (problem?.jiraIssueTypeName || '').trim().toLowerCase();
  return issueTypeName === 'subtask' || issueTypeName === 'sub-task';
}

function getTicketStatusSpaceKey(spaceName, statusName) {
  return `${(spaceName || '').trim().toLowerCase()}::${(statusName || '').trim().toLowerCase()}`;
}

export function getJiraInstanceHostFromTicketUrl(ticketUrl) {
  if (!ticketUrl) {
    return null;
  }

  try {
    return new URL(ticketUrl).host;
  } catch {
    return null;
  }
}

/** Mapping problems that apply to the given vuln tickets (matched by space name + status). */
export function getJiraStatusMappingProblemsForTickets(tickets = [], spaces = [], instanceUrl) {
  if (!tickets.length || !spaces.length) {
    return [];
  }

  const ticketKeys = new Set(tickets.map(ticket => getTicketStatusSpaceKey(ticket.spaceName, ticket.status)));
  const host = instanceUrl || getJiraInstanceHostFromTicketUrl(tickets.find(ticket => ticket.url)?.url);

  return flattenJiraStatusMappingProblems(spaces, host).filter(
    problem =>
      problem.category === JIRA_STATUS_MAPPING_PROBLEM_CATEGORY.STATUS_MAPPING &&
      ticketKeys.has(getTicketStatusSpaceKey(problem.spaceName, problem.jiraStatusName))
  );
}

function normalizeMatchValue(value) {
  return (value || '').trim().toLowerCase();
}

export function connectedSpaceMatchesProblemSpace(connectedSpace, problemSpace) {
  if (!connectedSpace || !problemSpace) {
    return false;
  }

  if (connectedSpace.id && problemSpace.jiraSpaceId && connectedSpace.id === problemSpace.jiraSpaceId) {
    return true;
  }

  const connectedKey = normalizeMatchValue(connectedSpace.projectKey);
  const problemKey = normalizeMatchValue(problemSpace.projectKey);
  if (connectedKey && problemKey && connectedKey === problemKey) {
    return true;
  }

  const connectedName = normalizeMatchValue(connectedSpace.name);
  const problemName = normalizeMatchValue(problemSpace.projectName);
  return Boolean(connectedName && problemName && connectedName === problemName);
}

export function getProblemSpaceForConnectedSpace(connectedSpace, problemSpaces = []) {
  return problemSpaces.find(problemSpace => connectedSpaceMatchesProblemSpace(connectedSpace, problemSpace)) ?? null;
}

export function connectedSpaceHasStatusMappingProblems(connectedSpace, problemSpaces = []) {
  const problemSpace = getProblemSpaceForConnectedSpace(connectedSpace, problemSpaces);
  if (!problemSpace) {
    return false;
  }

  return Boolean((problemSpace.mappingProblems?.length || 0) + (problemSpace.spaceProblems?.length || 0));
}

/** Keep only problem spaces that match a currently connected/added Jira space. */
export function filterJiraStatusMappingProblemsToConnectedSpaces(problemSpaces = [], connectedSpaces = []) {
  if (!problemSpaces.length || !connectedSpaces.length) {
    return [];
  }

  return problemSpaces.filter(problemSpace =>
    connectedSpaces.some(connectedSpace => connectedSpaceMatchesProblemSpace(connectedSpace, problemSpace))
  );
}

/** Connected spaces with mapping problems first, then the rest in original order. */
export function sortConnectedSpacesByStatusMappingProblems(connectedSpaces = [], problemSpaces = []) {
  if (!connectedSpaces.length || !problemSpaces.length) {
    return connectedSpaces;
  }

  const withProblems = [];
  const withoutProblems = [];

  connectedSpaces.forEach(space => {
    if (connectedSpaceHasStatusMappingProblems(space, problemSpaces)) {
      withProblems.push(space);
    } else {
      withoutProblems.push(space);
    }
  });

  return [...withProblems, ...withoutProblems];
}

/** Returns mapping problem codes keyed by workflow Jira status id. */
export function getStatusMappingProblemCodesByJiraStatusId(mappingProblems = [], jiraStatuses = []) {
  const codesByStatusId = {};

  jiraStatuses.forEach(jiraStatus => {
    const problem = mappingProblems.find(
      mappingProblem =>
        (mappingProblem.jiraStatusId && mappingProblem.jiraStatusId === jiraStatus.id) ||
        normalizeMatchValue(mappingProblem.jiraStatusName) === normalizeMatchValue(jiraStatus.name)
    );

    if (problem?.code) {
      codesByStatusId[jiraStatus.id] = problem.code;
    }
  });

  return codesByStatusId;
}
